import React, { useMemo, useState } from "react";
import { UploadDocumentDocuTypeEnum, UploadDocumentMetadataDto } from "../../api-client";

import {
  Grid,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  List,
  ListItem,
  Typography,
  CircularProgress,
} from "@mui/material";
import FileUpload from "../../molecules/FileUpload";
import FloatingActionButton from "../../molecules/FloatingButton";
import CameraCapture from "./capture/cameraCapture/CameraCapture";
import FileCapture from "./capture/fileCapture/FileCapture";
import Loading from "../../molecules/Loading";
import { AttachFile } from "@mui/icons-material";
import { mobiliTheme } from "../../themes/mobiliTheme";
import notificationStore from "../../stores/Notification/notificationStore";
import { TransactionsContext } from "../../stores/Transactions/Transactions.provider";
import useStore from "../../helpers/useStore";
import DocumentTypeSelect from "../Documents/DocumentTypeSelect";
import { isImageFile } from "../../utils/DocumentUtils";

interface DocumentUploadProps {
  documentMetaData: UploadDocumentMetadataDto;
  setDocumentsLoading: (value: boolean) => void;
  documentsLoading: boolean;
  uploadId?: string;
}

const DocumentUpload = ({
  documentMetaData,
  setDocumentsLoading,
  documentsLoading,
  uploadId,
}: DocumentUploadProps) => {
  const { uploadDocument, isUploading } = FileUpload();
  const TransactionStore = useStore(TransactionsContext);
  const { uploadSignedDocument } = TransactionStore;
  const [showDocuTypeModal, setShowDocuTypeModal] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [selectedDocuType, setSelectedDocuType] = useState<UploadDocumentDocuTypeEnum | null>(null);
  const [scanToPdf, setScanToPdf] = useState(false);

  const isCurrentFileImage = useMemo(() => isImageFile(files[0]), [files]);

  const closeModal = () => {
    setShowDocuTypeModal(false);
  };

  // Open the modal for a freshly-selected file, resetting the transient state so it
  // never inherits the previous upload's document type or scan checkbox.
  const handleCapture = (file: File) => {
    setFiles([file]);
    setSelectedDocuType(null);
    setScanToPdf(isImageFile(file));
    setShowDocuTypeModal(true);
  };

  const handleDismiss = () => {
    closeModal();
    setSelectedDocuType(null);
    setScanToPdf(false);
    return Promise.resolve(true);
  };

  const handleConfirm = async () => {
    try {
      if (!selectedDocuType) {
  return notificationStore.showMessage("Bitte wähle einen Dokumenttyp.", "warning");
      }

      if (!files || files.length === 0) {
        return notificationStore.showMessage(
          "Keine Datei ausgewählt. Bitte lade ein Dokument hoch.",
          "error"
        );
      }

      const file = files[0];

      documentMetaData.docuType = selectedDocuType;
      documentMetaData.scanToSearchablePdf = scanToPdf;

      // Upload document
      const response = uploadId
        ? await uploadSignedDocument(uploadId, { ...documentMetaData, file })
        : await uploadDocument(file, documentMetaData);

      // Refresh documents state after upload
      setDocumentsLoading(!documentsLoading);

      closeModal();
    } catch (error: any) {
      notificationStore.showMessage(error.message || "An unexpected error occurred.", "error");
    }
  };

  // Memoize the file list rendering
  const fileItems = useMemo(() => {
    return files.map((file, index) => (
      <ListItem key={file.name + index}>
        <Typography sx={{ fontWeight: "bold", cursor: "pointer" }}>
          <a
            href={URL.createObjectURL(file)}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "underline" }}
          >
            {file.name}
          </a>
        </Typography>
      </ListItem>
    ));
  }, [files]);

  const docuTypes = useMemo(() => {
    const docuValues = Object.values(UploadDocumentDocuTypeEnum);
    return docuValues.map((docuType) => (
      <FormControlLabel
        key={docuType}
        control={<Radio sx={{ padding: "6px" }} />}
        label={docuType}
        value={docuType}
      />
    ));
  }, [UploadDocumentDocuTypeEnum]);

  return (
    <>
      <Loading showLoading={isUploading} title="Uploading..." />
      {!uploadId && (
        <Grid container spacing={2} justifyContent="center">
          <Grid item xs={12}>
            <FloatingActionButton
              mainIcon={<AttachFile />}
              icons={[
                {
                  icon: (
                    <CameraCapture
                      onCapture={handleCapture}
                    />
                  ),
                  onClick: () => {},
                },
                {
                  icon: (
                    <FileCapture
                      onCapture={handleCapture}
                    />
                  ),
                  onClick: () => {},
                },
              ]}
            />
          </Grid>
        </Grid>
      )}
      <Dialog open={showDocuTypeModal} onClose={closeModal}>
        <DialogTitle>Art des Dokuments</DialogTitle>
        {/* <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle1">Ausgewählte Datei:</Typography>
              <List>{fileItems}</List>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1">Dokumenttyp auswählen:</Typography>
              <RadioGroup
                // sx={{ display: "flex", padding: "4px", margin: "3px" }}
                value={selectedDocuType}
                onChange={(e: any) => setSelectedDocuType(e.target.value)}
                aria-label="selectedDocuType"
                name="selectedDocuType"
              >
                {docuTypes}
              </RadioGroup>
            </Grid>
          </Grid>
        </DialogContent> */}
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle1">Ausgewählte Datei:</Typography>
              <List>{fileItems}</List>
            </Grid>
            <Grid item xs={12}>
              <DocumentTypeSelect
                selectedDocuType={selectedDocuType}
                onSelectDocumentType={(dType) => setSelectedDocuType(dType)}
              />
            </Grid>
            {isCurrentFileImage && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      sx={{ padding: "6px" }}
                      checked={scanToPdf}
                      onChange={(e) => setScanToPdf(e.target.checked)}
                    />
                  }
                  label="Als durchsuchbares PDF scannen (Zuschnitt + Texterkennung)"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleDismiss} variant="text">
            Abbrechen
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={!(selectedDocuType && files.length)}
          >
            {isUploading ? <CircularProgress size={24} /> : "Hochladen"}
          </Button>
        </DialogActions>
      </Dialog>

      {uploadId && (
        <FileCapture
          onCapture={handleCapture}
        />
      )}
    </>
  );
};

export default DocumentUpload;
